import React from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../../common/SafeIcon';

const { FiClock, FiLogOut, FiRefreshCw } = FiIcons;

/**
 * Modal component that warns users their session is about to expire
 */
const SessionTimeoutWarning = ({
  isOpen,
  timeRemaining,
  onExtendSession,
  onLogout
}) => {
  if (!isOpen) return null;

  return (
    <AnimatePresence>
      {isOpen && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          className="fixed inset-0 z-[100] flex items-center justify-center bg-black/60 backdrop-blur-sm"
        >
          <motion.div
            initial={{ scale: 0.9, opacity: 0, y: 20 }}
            animate={{ scale: 1, opacity: 1, y: 0 }}
            exit={{ scale: 0.9, opacity: 0, y: 20 }}
            transition={{ type: 'spring', damping: 25, stiffness: 300 }}
            className="bg-white rounded-2xl shadow-2xl p-8 max-w-md w-full mx-4"
          >
            {/* Warning Icon */}
            <div className="flex justify-center mb-6">
              <motion.div
                animate={{
                  scale: [1, 1.1, 1],
                }}
                transition={{
                  duration: 1.5,
                  repeat: Infinity,
                  ease: "easeInOut"
                }}
                className="w-20 h-20 bg-orange-100 rounded-full flex items-center justify-center"
              >
                <SafeIcon icon={FiClock} className="w-10 h-10 text-orange-500" />
              </motion.div>
            </div>

            {/* Title */}
            <h2 className="text-2xl font-bold text-gray-900 text-center mb-2">
              Session Expiring Soon
            </h2>

            {/* Description */}
            <p className="text-gray-600 text-center mb-6">
              Your session will expire due to inactivity. Would you like to stay logged in?
            </p>

            {/* Countdown Timer */}
            <div className="bg-gray-100 rounded-xl p-4 mb-6">
              <div className="text-center">
                <p className="text-sm text-gray-500 mb-1">Time remaining</p>
                <motion.p
                  key={timeRemaining}
                  initial={{ scale: 1.2 }}
                  animate={{ scale: 1 }}
                  className="text-4xl font-mono font-bold text-orange-600"
                >
                  {timeRemaining}
                </motion.p>
              </div>
            </div>

            {/* Action Buttons */}
            <div className="flex flex-col gap-3">
              <motion.button
                whileHover={{ scale: 1.02 }}
                whileTap={{ scale: 0.98 }}
                onClick={onExtendSession}
                className="w-full flex items-center justify-center gap-2 px-6 py-3 bg-primary-600 hover:bg-primary-700 text-white font-semibold rounded-xl transition-colors shadow-lg shadow-primary-500/25"
              >
                <SafeIcon icon={FiRefreshCw} className="w-5 h-5" />
                Stay Logged In
              </motion.button>

              <motion.button
                whileHover={{ scale: 1.02 }}
                whileTap={{ scale: 0.98 }}
                onClick={onLogout}
                className="w-full flex items-center justify-center gap-2 px-6 py-3 bg-gray-100 hover:bg-gray-200 text-gray-700 font-semibold rounded-xl transition-colors"
              >
                <SafeIcon icon={FiLogOut} className="w-5 h-5" />
                Log Out Now
              </motion.button>
            </div>

            {/* Footer note */}
            <p className="text-xs text-gray-400 text-center mt-4">
              For your security, you will be automatically logged out after the timer expires.
            </p>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
};

export default SessionTimeoutWarning;
